import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader, EmptyState, ConfirmDelete, Panel, MobileCards, MobileCard } from "@/components/common";
import { FieldGrid } from "@/components/Field";

// Generic master-data page: sections=[{title, fields}], columns=[{label, render}]
export default function CrudPage({ resource, eyebrow, title, subtitle, singular, sections, columns, icon, defaults = {}, renderCard }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/${resource}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startNew = () => { setForm({ ...defaults }); setOpen(true); };
  const startEdit = (it) => { setForm({ ...it }); setOpen(true); };
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.put(`/${resource}/${form.id}`, form);
      else await api.post(`/${resource}`, form);
      toast.success(`${singular} saved`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/${resource}/${id}`);
      toast.success(`${singular} deleted`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const filtered = items.filter((it) => !q || JSON.stringify(it).toLowerCase().includes(q.toLowerCase()));
  const tp = resource;

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        subtitle={subtitle}
        actions={
          <Button onClick={startNew} data-testid={`${tp}-add-btn`} className="h-11 sm:h-10">
            <Plus className="h-4 w-4 mr-1.5" /> Add {singular}
          </Button>
        }
      />
      <Panel>
        <div className="relative max-w-sm mb-4">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${title.toLowerCase()}...`}
            className="pl-9 h-11 sm:h-10" data-testid={`${tp}-search-input`} />
        </div>
        {!loading && filtered.length === 0 ? (
          <EmptyState icon={icon} title={`No ${title.toLowerCase()} yet`} text={`Add your first ${singular.toLowerCase()} to get started.`}
            action={<Button variant="outline" onClick={startNew}><Plus className="h-4 w-4 mr-1" /> Add {singular}</Button>} />
        ) : renderCard ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((it) => renderCard(it, { edit: () => startEdit(it), remove: () => remove(it.id) }))}
          </div>
        ) : (<>
          <MobileCards items={filtered} testid={`${tp}-mobile-list`} render={(it) => (
            <MobileCard key={it.id} testid={`${tp}-card-${it.id}`} title={columns[0].render(it)}
              rows={columns.slice(1).map((c) => [c.label, c.render(it)])}
              actions={<>
                <Button size="sm" variant="outline" className="h-10 flex-1" onClick={() => startEdit(it)} data-testid={`${tp}-card-edit-${it.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                <ConfirmDelete onConfirm={() => remove(it.id)} trigger={<Button size="sm" variant="outline" className="h-10 text-rose-600" data-testid={`${tp}-card-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>} />
              </>} />
          )} />
          <div className="hidden md:block overflow-x-auto -mx-4 sm:-mx-5">
            <table className="w-full text-sm" data-testid={`${tp}-table`}>
              <thead>
                <tr className="border-b text-left">
                  {columns.map((c) => (
                    <th key={c.label} className="px-5 py-2.5 eyebrow whitespace-nowrap">{c.label}</th>
                  ))}
                  <th className="px-5 py-2.5 eyebrow text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b last:border-0 hover:bg-muted/50 transition-colors" data-testid={`${tp}-row-${it.id}`}>
                    {columns.map((c) => (
                      <td key={c.label} className="px-5 py-3 align-top">{c.render(it)}</td>
                    ))}
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => startEdit(it)} data-testid={`${tp}-edit-${it.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <ConfirmDelete onConfirm={() => remove(it.id)}
                        trigger={<Button variant="ghost" size="icon" className="text-rose-600" data-testid={`${tp}-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>)}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[90dvh] overflow-y-auto bg-card p-4 sm:p-6" data-testid={`${tp}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? `Edit ${singular}` : `New ${singular}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {sections.map((s) => (
              <div key={s.title}>
                <div className="eyebrow mb-3">{s.title}</div>
                <FieldGrid fields={s.fields} data={form} onChange={onChange} testPrefix={tp} />
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sticky -bottom-4 sm:-bottom-6 bg-card py-3 border-t">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`${tp}-save-btn`}>{saving ? "Saving..." : `Save ${singular}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
